'use client';

import React, { useState, useRef, useEffect, useContext } from 'react';
import { QuestionBar, isFilterTooBig } from './question-bar';
import { Welcome } from './welcome';
import { AnswerItem, Answer, EventFromChat } from './answer-item';
import { CaptureError, FetchWithVersionCheck, getCaseDisplayName, getTokenInfo, getUiSourceFromChatSource, getUsername } from '@/shared/functions';
import { dedupeSources, useWindowSize } from '@/shared/clientFunctions';
import { SourceObject, Source, LLMConfig, ScopesObject, Slide, FeatureFlagTypes } from '@/shared/types';
import { REFUSAL_MESSAGES, PASS_MESSAGES, LLM_CONFIG, MODEL_ARNS_LIST, TINY_VIEWPORT_WIDTH } from '@/shared/constants';
import { Button } from '@heroui/react';
import { Tooltip } from '@heroui/tooltip';
import { ChatSessions } from './chat-sessions';
import { CaretDoubleUpIcon, DotsThreeOutlineIcon } from '@phosphor-icons/react';
import { AuthContext, FullScreenContext } from '../app/contexts';
import { Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/modal';

// Map file types to BKB ObjectCategory values for content filtering
const fileTypeToCategoryMap: Record<string, string> = {
  'PDF': 'PDF',
  'MS Office': 'OFFICE',
  'Image': 'IMAGE',
  'Video': 'VIDEO',
  'Audio': 'AUDIO',
  'Spreadsheet': 'SPREADSHEET',
  'Email': 'EMAIL',
  'Other': 'OTHER'
};
interface ChatProps {
  selectedOption: string;
  userId: string;
  userName: string;
  updateNewAnswer: (newAnswer: Answer) => void;
  addToTimeline: (event: EventFromChat) => void;
  addMultipleToTimeline: (events: Slide[], useEnrichment: boolean, edit: boolean) => void;
  isEditableTimeline: boolean;
  setSourcesFromChat: (sources: SourceObject[]) => void;
  swapSessionId: (sessionId: string | null, newSessionId: string) => Promise<string | null>;
  setMainChatSessionId: (sessionId: string | null) => void;
  entityExtractionJobs: any[];
  chatHistorySessions: any[] | null;
  createEventsFromEntityEvents: (jobId: string, events: any[], enriching: boolean, timelineIdIn?: string) => Promise<void>;
  getEventsFromEntityService: (action: string, timelineId: string | null, caseId: string, answerItemText: string, sources: Source[], epochTimeSecs: number, jobId: string | null, cancelPolling: boolean) => Promise<void>;
  featureFlags: {
    [key: string]: FeatureFlagTypes;
  };
}
export interface AnswerItemFromHistory {
  question: string;
  answer: string;
  session_id: string;
  time_saved: number;
  scopes: string[];
  files: string[];
  excluded_scopes: string[];
  excluded_files: string[];
  epoch_time_secs: number;
}
const RETRY_MESSAGE = 'TimePilot was unable to answer this question at this time. Please refresh the page and try again. If the issue persists, please provide feedback using the button in the lower-right.';
export const Chat: React.FC<ChatProps> = ({
  selectedOption,
  userName,
  userId,
  updateNewAnswer,
  addToTimeline,
  addMultipleToTimeline,
  isEditableTimeline,
  setSourcesFromChat,
  swapSessionId,
  setMainChatSessionId,
  entityExtractionJobs,
  chatHistorySessions,
  createEventsFromEntityEvents,
  getEventsFromEntityService,
  featureFlags
}) => {
  const {
    width
  } = useWindowSize();
  const isTinyViewport = width !== undefined && width < TINY_VIEWPORT_WIDTH;
  const [answers, setAnswers] = useState<undefined | Answer[]>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [previousSessionId, setPreviousSessionId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null); // Create a ref for the textarea
  // const [totalTimeSaved, setTotalTimeSaved] = useState(0);
  // const [historyError, setHistoryError] = useState<string | null>(null);
  const [previousSelectedOption, setPreviousSelectedOption] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // Define isExpanded state
  const [showSources, setShowSources] = useState(false); // Define showSources state
  const [refusalMessages, setRefusalMessages] = useState<string[]>(REFUSAL_MESSAGES);
  const [passMessages, setPassMessages] = useState<string[]>(PASS_MESSAGES);
  const [modelArnsList, setModelArnsList] = useState<any>(null);
  const [llmConfig, setLlmConfig] = useState<LLMConfig>(LLM_CONFIG);
  // const [processedSources, setProcessedSources] = useState<SourceObject[]>([]);
  const [processedSourcesMap, setProcessedSourcesMap] = useState<{
    [key: number]: SourceObject[];
  }>({});
  const [showChatSessions, setShowChatSessions] = useState<boolean>(false);
  const [showFolders, setShowFolders] = useState(false);
  const [allFileTypes, setAllFileTypes] = useState(false);
  const [activeFileTypes, setActiveFileTypes] = useState({});
  const [evidenceFilter, setEvidenceFilter] = useState<string>('');
  const [scopesAndFiles, setScopesAndFiles] = useState<ScopesObject>(JSON.parse(localStorage.getItem(`${selectedOption}-scopesObject`) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }') as ScopesObject);
  const [extractionJobs, setExtractionJobs] = useState<any[]>([]);
  const [isSwapping, setIsSwapping] = useState<boolean>(false);
  const [triggerPromptLibrary, setTriggerPromptLibrary] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [preliminaryResult, setPreliminaryResult] = useState<{
    query: string;
    answer: string;
  } | null>(null);
  const [isExporting, setIsExporting] = useState(0);
  const {
    isFullscreen,
    setIsFullscreen,
    setFullscreenMediaItem
  } = useContext(FullScreenContext);
  const answersContainerRef = useRef<HTMLDivElement>(null);
  const currentAnswerIndex = useRef<number | undefined>(undefined);
  const currentSessionId = useRef<string | null>(null);
  const {
    idToken,
    accessToken,
    tenantName
  } = useContext(AuthContext);
  const handleShowChatSessions = (show: boolean) => {
    setShowChatSessions(show);
  };
  const handleSetScopesAndFiles = (scopesAndFiles: ScopesObject) => {
    setScopesAndFiles(scopesAndFiles);
    if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, selectedOption)) {
      return;
    }
    localStorage.setItem(`${selectedOption}-scopesObject`, JSON.stringify(scopesAndFiles));
  };
  const handleExportAnswerAsPdf = async (epochTimeSecs: number, answerElement: HTMLDivElement) => {
    setIsExporting(epochTimeSecs);
    if (answerElement) {
      const html2pdf = (await import("html2pdf.js")).default;
      const opt: any = {
        margin: [5, 5, 5, 5],
        pagebreak: {
          mode: ['avoid-all']
        },
        jsPDF: {
          orientation: 'portrait',
          format: 'letter'
        }
      };
      await html2pdf().set(opt).from(answerElement).save(`${getCaseDisplayName(selectedOption)}-Answer.pdf`);
    }
    setIsExporting(0);
  };
  const addSourceToScope = (path: string) => {
    const newScopesAndFiles = {
      ...scopesAndFiles,
      files: Array.from(new Set([...scopesAndFiles.files, path]))
    };
    handleSetScopesAndFiles(newScopesAndFiles);
  };
  useEffect(() => {
    setExtractionJobs(entityExtractionJobs);
  }, [entityExtractionJobs]);
  useEffect(() => {
    handleSetScopesAndFiles(JSON.parse(localStorage.getItem(`${selectedOption}-scopesObject`) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }') as ScopesObject);
    setIsExpanded(false);
    setIsFullscreen(false);
    setPreliminaryResult(null);
    setAnswers(undefined);
    setTriggerPromptLibrary(0);
    // Close sessions panel when switching cases so loadChatHistory can run and the UI can recover
    handleShowChatSessions(false);
  }, [selectedOption]);
  useEffect(() => {
    const handler = (e: CustomEvent<{
      caseId: string;
    }>) => {
      if (e.detail?.caseId === selectedOption) {
        setScopesAndFiles(JSON.parse(sessionStorage.getItem(`${selectedOption}-scopesObject`) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }') as ScopesObject);
      }
    };
    window.addEventListener('scopes-updated', handler as EventListener);
    return () => window.removeEventListener('scopes-updated', handler as EventListener);
  }, [selectedOption]);
  const switchToSession = async (newSessionId: string) => {
    setAnswers(undefined);
    setSessionId(null);
    handleShowChatSessions(false);
    // setTotalTimeSaved(0);
    setTriggerPromptLibrary(0);
    const oldSessionId = sessionId;
    setIsSwapping(true);
    const sessionReturned = await swapSessionId(oldSessionId, newSessionId);
    setPreviousSessionId('SWAP');
    setIsSwapping(false);
    setSessionId(sessionReturned ?? null);
  };
  const handleCloseSessions = () => {
    handleShowChatSessions(false);
  };
  useEffect(() => {
    const modelArnsListFromFeatureFlag = featureFlags?.['modelArnsList'];
    if (modelArnsListFromFeatureFlag) {
      setModelArnsList(modelArnsListFromFeatureFlag);
    } else if (featureFlags?.['systemStage']) {
      const systemStage = featureFlags?.['systemStage'] as string;
      const modelArnsListHardcoded = MODEL_ARNS_LIST(systemStage);
      setModelArnsList(modelArnsListHardcoded);
    } else {
      const modelArnsListHardcoded = MODEL_ARNS_LIST('prod');
      setModelArnsList(modelArnsListHardcoded);
    }

    // Refusal Messages from the llmConfig feature flag take precedence, so check both
    // this code is currently duplicated in answer-item.tsx
    const refusalMessagesFromFeatureFlag: any = featureFlags?.refusalMessages;
    const passMessagesFromFeatureFlag: any = featureFlags?.passMessages;
    const llmConfigFromFeatureFlag = featureFlags?.llmConfig;
    const llmConfigRefusalMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.refusalMessages;
    const llmConfigPassMessages = (llmConfigFromFeatureFlag as unknown as LLMConfig)?.passMessages;
    const refusalMessagesArray = refusalMessagesFromFeatureFlag?.refusalMessages && Array.isArray(refusalMessagesFromFeatureFlag.refusalMessages) ? refusalMessagesFromFeatureFlag.refusalMessages : [];
    const passMessagesArray = passMessagesFromFeatureFlag?.passMessages && Array.isArray(passMessagesFromFeatureFlag.passMessages) ? passMessagesFromFeatureFlag.passMessages : [];
    const llmConfigRefusalMessagesArray = Array.isArray(llmConfigRefusalMessages) ? llmConfigRefusalMessages : [];
    const llmConfigPassMessagesArray = Array.isArray(llmConfigPassMessages) ? llmConfigPassMessages : [];
    if (llmConfigRefusalMessagesArray.length) {
      setRefusalMessages(llmConfigRefusalMessagesArray);
    } else if (refusalMessagesArray.length) {
      setRefusalMessages(refusalMessagesArray);
    } else {
      setRefusalMessages(REFUSAL_MESSAGES);
    }
    if (llmConfigPassMessagesArray.length) {
      setPassMessages(llmConfigPassMessagesArray);
    } else if (passMessagesArray.length) {
      setPassMessages(passMessagesArray);
    } else {
      setPassMessages(PASS_MESSAGES);
    }

    // LLM Config from the llmConfig feature flag takes precedence and overrides the refusalMessages flag too
    if (llmConfigFromFeatureFlag) {
      const lDllmConfig: LLMConfig = llmConfigFromFeatureFlag as unknown as LLMConfig;
      if (lDllmConfig.refusalMessages !== undefined && lDllmConfig.passMessages !== undefined && lDllmConfig.topP !== undefined && lDllmConfig.textResponsePartDelimiter !== undefined && lDllmConfig.overheadPerPair !== undefined && lDllmConfig.markdownDirective !== undefined && lDllmConfig.promptTemplate !== undefined && lDllmConfig.conversationContext !== undefined && lDllmConfig.promptTemplateWithContext !== undefined && lDllmConfig.orchestrationPromptTemplate !== undefined && lDllmConfig.historyWordLimit !== undefined && lDllmConfig.historyCharLimit !== undefined && lDllmConfig.temperature !== undefined && lDllmConfig.unalignedTemperature !== undefined && lDllmConfig.maxTokens !== undefined && lDllmConfig.numOfResults !== undefined && lDllmConfig.overrideSearchType !== undefined && lDllmConfig.supportedQueryTransformationConfigTypes !== undefined) {
        setLlmConfig(lDllmConfig as unknown as LLMConfig);
      }
    }
  }, [featureFlags]);

  // this loads chat history and defines answers so we know the laoding state
  const loadChatHistory = async () => {
    if (!selectedOption) {
      return;
    }
    let formattedAnswers: Answer[] = [];
    try {
      const historyResponse = await FetchWithVersionCheck('/api/protected/chat/history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: userId,
          caseId: selectedOption
        })
      });
      const history = await historyResponse.json();
      const isHistoryArray = Array.isArray(history);
      if (isHistoryArray && history.length > 0) {
        setIsLoading(false);
        formattedAnswers = history.map((item: AnswerItemFromHistory) => {
          const answer = JSON.parse(item.answer);
          let answerText = !!answer && 'output' in answer ? answer.output as string : RETRY_MESSAGE;
          const containsPreliminaryResults = !!answer && 'messages' in answer ? true : false;
          const isFinalResult = containsPreliminaryResults && 'is_preliminary' in answer && answer.is_preliminary === false || !containsPreliminaryResults;
          if (!isFinalResult && featureFlags?.showPreliminaryResult) {
            answerText = `PRELIMINARY_MESSAGES|${answer.messages.map((message: any) => message.content).join('\n')}`;
          }
          return {
            question: item.question,
            answerText: answerText,
            sources: answer.citations || [],
            epochTimeSecs: item.epoch_time_secs,
            sessionId: item.session_id,
            timeSaved: item.time_saved,
            resultForFeedback: item,
            scopesObject: {
              scopes: item.scopes || [],
              files: item.files || [],
              excludedScopes: item.excluded_scopes || [],
              excludedFiles: item.excluded_files || []
            }
          };
        });
        setSessionId(formattedAnswers.length > 0 ? formattedAnswers[formattedAnswers.length - 1].sessionId : null);
        setMainChatSessionId(formattedAnswers.length > 0 ? formattedAnswers[formattedAnswers.length - 1].sessionId : null);
        const lastScopesObject = formattedAnswers.length > 0 ? formattedAnswers[formattedAnswers.length - 1].scopesObject : null;
        if (lastScopesObject) {
          localStorage.setItem(`${selectedOption}-scopesObject`, JSON.stringify(lastScopesObject));
          handleSetScopesAndFiles(lastScopesObject);
        }
        setAnswers(formattedAnswers);
        setPreviousSessionId(sessionId);
      } else if (history && 'error' in history) {
        setAnswers([]);
        setSessionId(null);
        setMainChatSessionId(null);
        const errorMessage = `Error loading chat history for user ${userId} on ${selectedOption}: ${history.error}`;
        CaptureError(errorMessage, new Error(history.error), !!featureFlags?.useIntercom);
      } else {
        const savedScopesAndFiles = JSON.parse(localStorage.getItem(`${selectedOption}-scopesObject`) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }') as ScopesObject;
        handleSetScopesAndFiles(savedScopesAndFiles);
        setAnswers([]);
        setSessionId(null);
        setMainChatSessionId(null);
      }
    } catch (error) {
      setAnswers([]);
      setSessionId(null);
      setMainChatSessionId(null);
      const errorMessage = `Error loading chat history for user ${userId} on ${selectedOption}: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
    }
    // Update the previous selected option
    if (selectedOption !== previousSelectedOption) {
      setPreviousSelectedOption(selectedOption);
    }
    return;
  };
  useEffect(() => {
    if (selectedOption !== previousSelectedOption || previousSessionId !== sessionId) {
      // forceLoad: true so we load even if sessions panel is still open (setState from case switch is async)
      loadChatHistory();
      return;
    }
    loadChatHistory();
  }, [selectedOption, previousSelectedOption, previousSessionId, sessionId, showChatSessions]);

  // start polling when the user scrolls to the bottom of the chat answers container
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop + e.currentTarget.clientHeight >= e.currentTarget.scrollHeight) {
      loadChatHistory();
    }
  };
  const snapToTop = (index: number) => {
    if (answersContainerRef.current && !isFullscreen) {
      const element = document.getElementById(`answer-item-${index}`);
      if (element) {
        currentAnswerIndex.current = index;
        element.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        });
      }
    }
  };
  useEffect(() => {
    if (!answers) {
      return;
    }
    if (currentSessionId.current !== sessionId) {
      currentSessionId.current = sessionId;
      currentAnswerIndex.current = undefined;
    }
    const scrollPosition = answersContainerRef.current?.scrollTop || 0;
    const scrollHeight = answersContainerRef.current?.scrollHeight || 0;
    const clientHeight = answersContainerRef.current?.clientHeight || 0;
    const scrollHeightMinusClientHeight = scrollHeight - clientHeight;
    if (scrollPosition >= scrollHeightMinusClientHeight) {
      currentAnswerIndex.current = answers.length - 1;
    }
    snapToTop(currentAnswerIndex.current === undefined ? answers.length - 1 : currentAnswerIndex.current);
  }, [answers, sessionId]);
  const processSourcesFromChat = async () => {
    const newSourcesFromChat: SourceObject[] = [];
    const newProcessedSourcesMap: {
      [key: number]: SourceObject[];
    } = {};
    try {
      answers?.forEach(async (answer: Answer, index: number) => {
        const answerProcessedSources: SourceObject[] = [];
        answer.sources?.forEach(async (source: Source) => {
          const sourceObject = getUiSourceFromChatSource(source);
          answerProcessedSources.push(sourceObject);
          newSourcesFromChat.push(sourceObject);
        });
        newProcessedSourcesMap[index] = answerProcessedSources;
      });
      const dedupedSources = dedupeSources(newSourcesFromChat);
      setSourcesFromChat(dedupedSources);
      // setProcessedSources(newSourcesFromChat);
      setProcessedSourcesMap(newProcessedSourcesMap);
    } catch (error) {
      CaptureError('Error processing sources from chat', error as Error, !!featureFlags?.useIntercom);
    } finally {
      // do nothing
    }
  };
  useEffect(() => {
    processSourcesFromChat();
  }, [answers, sessionId, selectedOption]);
  const onQuerySubmit = async (query: string, scopesAndFiles: ScopesObject, userId: string, sessionId: string | null, retry: boolean = true, retryCount: number = 0, useDeepDive: boolean = false) => {
    const trimmedQuery = query.trim();
    const files = scopesAndFiles.files;
    const scopes = scopesAndFiles.scopes;
    const excludedFiles = scopesAndFiles.excludedFiles;
    const excludedScopes = scopesAndFiles.excludedScopes;
    const selectedContentCategories = scopesAndFiles.contentCategories || [];
    const contentCategories = selectedContentCategories.map(fileType => fileTypeToCategoryMap[fileType]).filter(Boolean);
    try {
      let tenant = null;
      if (accessToken) {
        const tokenInfo = getTokenInfo(accessToken);
        tenant = tokenInfo?.['custom:tenant'] || null;
      }
      const chatPath = '/api/protected/chat-service-proxy';
      const idTokenInfoForChat = idToken ? getTokenInfo(idToken) : null;
      const body: any = {
        query: trimmedQuery,
        deepDive: useDeepDive,
        threatReport: false,
        selectedCase: selectedOption,
        caseId: selectedOption,
        username: idTokenInfoForChat && getUsername(idTokenInfoForChat) || userName,
        files: files,
        scopes: scopes,
        excludedFiles: excludedFiles,
        excludedScopes: excludedScopes,
        userId: userId,
        sessionId: sessionId,
        retryCount: retryCount,
        refusalMessages: refusalMessages,
        passMessages: passMessages,
        modelArnsList: modelArnsList,
        llmConfig: llmConfig,
        useQueryDecomposition: false,
        useOpenSearch: !!featureFlags?.['useOpenSearchForChat'] || false,
        showPreliminaryResult: useDeepDive,
        useNewTimeSaved: featureFlags?.newTimeSaved || false,
        ...(featureFlags?.contentCategories && contentCategories.length > 0 && {
          contentCategories
        })
      };
      if (tenant) {
        body.tenant = tenant;
      }
      currentAnswerIndex.current = undefined;
      setIsLoading(true);
      try {
        const options: RequestInit = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify(body)
        };
        const response = await FetchWithVersionCheck(chatPath, options);
        if (response && response?.ok) {
          loadChatHistory();
          setIsLoading(false);
          return;
        }
      } catch (error) {
        loadChatHistory();
        setIsLoading(false);
        return;
      }
    } catch (error) {
      const errorMessage = 'Error querying TimePilot:';
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      // TODO: Need to handle error (e.g., show an error message to the user)
    }
    setTimeout(() => {
      loadChatHistory();
      setIsLoading(false);
    }, 5000);
  };
  const handleClearThread = async () => {
    if (isResetting) {
      setMainChatSessionId(null);
      setFullscreenMediaItem(null);
      return;
    }
    try {
      setIsResetting(true);
      const savedScopesAndFiles = JSON.parse(localStorage.getItem(`${selectedOption}-scopesObject`) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }') as ScopesObject;
      handleSetScopesAndFiles(savedScopesAndFiles);
      setShowFolders(false);
      setAllFileTypes(false);
      setActiveFileTypes({});
      await FetchWithVersionCheck('/api/protected/chat/history', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId: userId,
          caseId: selectedOption
        })
      });
      setIsResetting(false);
      setSessionId(null);
      setPreviousSessionId(null);
      setMainChatSessionId(null);
      setAnswers(undefined);
      setTriggerPromptLibrary(0);
    } catch (error) {
      setIsResetting(false);
      console.error('Error clearing chat history:', error);
      // TODO: Handle error (e.g., show an error message to the user)
    } finally {
      setIsResetting(false);
    }
  };
  const handleRedo = async (question: string) => {
    setIsExpanded(true);
    // Wait for the expansion animation to complete
    await new Promise(resolve => setTimeout(resolve, 500));
    setQuery(question.trim());
    textareaRef.current?.focus();
    if (textareaRef.current) {
      textareaRef.current.value = question.trim();
    }
  };
  const setCurrentScopesAndFiles = ({
    scopes,
    files,
    excludedScopes,
    excludedFiles
  }: ScopesObject) => {
    setIsExpanded(true);
    handleSetScopesAndFiles({
      scopes,
      files,
      excludedScopes,
      excludedFiles
    });
  };
  return <div data-testid="chat" className={`z-1 box-border flex flex-col relative justify-center items-center
        text-['#f9f9f9'] font-inter overflow-x-hidden
        ${isFullscreen ? 'h-[1px] w-[1px] max-h-[1px] max-w-[1px] overflow-y-hidden' : featureFlags?.enableMediaAndSourceShare && featureFlags?.workProductNav && selectedOption ? 'h-[calc(100vh-150px)] sm:h-[calc(100vh-186px)]' : 'h-[calc(100vh-26px)] sm:h-[calc(100vh-113px)]'}`} data-sentry-component="Chat" data-sentry-source-file="chat.tsx">
      {!isSwapping && !!answers && !selectedOption && <div className="flex flex-col items-center justify-center mt-[-100px] sm:mt-[50px]">
          <div id="logo" className="flex flex-row items-center justify-center gap-2 sm:gap-4 leading-normal tracking-normal mq480:flex-wrap mb-14 sm:mb-20">
              <img className="relative object-contain w-[300px] sm:w-[800px] h-[96px] sm:h-[280px] mb-[-50px]" alt="TimePilot logo" src="/tp-logotype.png" />
          </div>
          <Button onPress={() => setIsModalOpen(true)} className="flex mt-4 sm:mt-0 cursor-pointer text-medium sm:text-2xl py-2 sm:py-6 sm:px-8 px-3 font-light animate-glow [box-shadow:0_0_8px_rgba(89,162,247,0.6),0_0_16px_rgba(89,162,247,0.4),0_0_24px_rgba(89,162,247,0.3)]">Getting Started Guide</Button>
        </div>}
      <Modal isOpen={isModalOpen} onOpenChange={setIsModalOpen} data-sentry-element="Modal" data-sentry-source-file="chat.tsx">
        <ModalContent className="max-w-4xl max-h-[90vh]" data-sentry-element="ModalContent" data-sentry-source-file="chat.tsx">
          <ModalHeader className="flex flex-col gap-1" data-sentry-element="ModalHeader" data-sentry-source-file="chat.tsx">
          </ModalHeader>
          <ModalBody className="p-0" data-sentry-element="ModalBody" data-sentry-source-file="chat.tsx">
            <iframe src="https://tranquility.ai/get-started-guide" title="TimePilot Getting Started Guide" className="w-full h-[95vh] sm:h-[70vh] rounded-lg" />
          </ModalBody>
        </ModalContent>
      </Modal>
      {!!answers && selectedOption && !showChatSessions && !isFullscreen && <div className="z-[10009] sm:block hidden absolute left-[-53px] top-[65px] w-[30px] h-[80px]">
          <Button className="box-border flex flex-column justify-center items-center rotate-90 hover:bg-white/10 text-white/65 z-[10007] font-light tracking-wide border-0 border-b-3 border-white/50 hover:border-white border-double rounded-b-none rounded-t-md cursor-pointer bg-white/5 hover:text-white px-2 py-0 m-0" onPress={() => handleShowChatSessions(true)}>
            Session History
            <CaretDoubleUpIcon size={15} weight="bold" />
          </Button>
        </div>}
      {!isSwapping && !!answers && selectedOption && showChatSessions && !isFullscreen && <div className="z-[10009] box-border flex flex-col absolute left-0 top-0 w-[300px]
              h-[calc(95vh-66px)] sm:h-[calc(95vh-113px)] overflow-y-auto
            ">
          <ChatSessions caseId={selectedOption} userId={userId} sessionId={sessionId} chatSessions={chatHistorySessions} switchToSession={switchToSession} closeSessions={handleCloseSessions} />
        </div>}
      {!!isSwapping && !!answers && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>Switching sessions<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
        </div>}
      {!selectedOption && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>Please select a case to begin.</div>
        </div>}
      {!!selectedOption && !answers && <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold'>
          <div>Loading case history<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
        </div>}
      {!isSwapping && !!answers && selectedOption && <div id="chatAnswersContainer" ref={answersContainerRef} onScroll={handleScroll} className={`box-border
            ${isFullscreen ? '-z-10 w-[1px] max-w-[1px] max-h-[1px] h-[1px] overflow-y-hidden overflow-x-hidden' : 'w-[calc(100vw-20px)] z-[10008] overflow-x-hidden custom-scrollbar2 flex flex-col items-center h-full overflow-y-auto sm:flex-grow'}
            ${!answers || answers.length === 0 ? '' : featureFlags?.enableMediaAndSourceShare ? isTinyViewport ? 'pb-[300px]' : 'pb-[495px]' : isTinyViewport ? 'pb-[260px]' : 'pb-[445px]'}
            ${!!answers && answers.length > 0 || featureFlags?.showPreliminaryResult && !!preliminaryResult ? '' : 'justify-center'}
          `}>
          {(!answers || answers.length === 0) && <Welcome />}
          {(!!answers && answers.length > 0 || featureFlags?.showPreliminaryResult && !!preliminaryResult) && <div>
              {!!answers && answers.length > 0 && answers.filter((item: Answer) => item.answerText).map((answer, index) => <div key={index} className={`
                    ${showChatSessions ? 'w-[calc(100vw-250px)] ml-[250px]' : 'w-screen'}
                    box-border flex flex-col items-center gap-0 justify-center transition-opacity duration-500 opacity-100 pb-6
                  `}>
                  <div id={`answer-item-${index}`} className='flex h-[1px] w-[1px] background-transparent'>
                  </div>
                  <div className={`
                      ${showChatSessions ? 'w-[calc(100vw-250px)]' : 'w-screen'}
                      box-border flex items-center justify-center transition-opacity duration-500 opacity-100 pb-6
                    `}>
                    <AnswerItem key={index} userId={userId} caseId={selectedOption} answerIndex={index} question={answer.question.replace(/^strands-/, '').replace(/^threat-report-/, '')} answerText={answer.answerText} sources={processedSourcesMap[index] || answer.sources} epochTimeSecs={answer.epochTimeSecs} onRedo={handleRedo} addToTimeline={addToTimeline} addMultipleToTimeline={addMultipleToTimeline} isEditableTimeline={isEditableTimeline} featureFlags={featureFlags || {}} resultForFeedback={answer.resultForFeedback} scopesObject={answer.scopesObject} setCurrentScopesAndFiles={setCurrentScopesAndFiles} addSourceToScope={addSourceToScope} onSendToQuery={setQuery} onFilterEvidence={setEvidenceFilter} setIsExpanded={setIsExpanded} extractionJob={extractionJobs.find(job => Number(job.epochTimeSecs) === Number(answer.epochTimeSecs)) || {
              status: 'dummy_status'
            }} createEventsFromEntityEvents={createEventsFromEntityEvents} getEventsFromEntityService={getEventsFromEntityService} snapToTop={() => snapToTop(index)} handleExportAnswerAsPdf={handleExportAnswerAsPdf} isExporting={isExporting === answer.epochTimeSecs ? true : false} />
                  </div>
                </div>)}
            </div>}
          <div className={`z-[10007] box-border flex flex-col items-center justify-center w-screen ${isSwapping || !answers ? 'w-1 h-1' : ''}`}>
            <QuestionBar caseId={selectedOption} onSubmit={onQuerySubmit} userId={userId} sessionId={sessionId} answers={answers || []} hasChatHistory={(answers?.length || 0) > 0} onClearThread={handleClearThread} isLoading={isLoading} isResetting={isResetting} setQuery={setQuery} query={query} textareaRef={textareaRef} isExpanded={!answers || answers.length === 0 ? true : isExpanded} setIsExpanded={setIsExpanded} collapseSources={() => setShowSources(false)} scopesAndFiles={scopesAndFiles} setScopesAndFiles={handleSetScopesAndFiles} showFolders={showFolders} setShowFolders={setShowFolders} allFileTypes={allFileTypes} setAllFileTypes={setAllFileTypes} activeFileTypes={activeFileTypes} setActiveFileTypes={setActiveFileTypes} evidenceFilter={evidenceFilter} addSourceToScope={addSourceToScope} addToTimeline={addToTimeline} featureFlagsProp={featureFlags} triggerPromptLibrary={triggerPromptLibrary} />
          </div>
          {!isSwapping && !!answers && !isFullscreen && (!answers || answers.length === 0) && featureFlags?.suggestedPrompts && (featureFlags?.suggestedPrompts as unknown as {
        label: string;
        query: string;
      }[]).length > 0 &&
      // <Walkthru walkthruName="intro1" index={0} placement="bottom" width="350px">
      <div className="box-border flex flex-col items-center justify-start sm:justify-center w-screen mt-4">
                <div className="box-border flex flex-row px-[20px] sm:px-0 items-center justify-start sm:justify-center w-screen gap-2 sm:gap-4 scrollbar-hide sm:overflow-x-hidden overflow-x-auto">
                  <h3 className="sm:flex hidden text-white/65 text-lg font-normal sm:mb-4 mb-1">Suggested prompts:</h3>
                  {(featureFlags.suggestedPrompts as unknown as {
            label: string;
            query: string;
          }[]).map((prompt, index) => <button key={index} onClick={() => {
            textareaRef.current?.focus();
            if (textareaRef.current) {
              textareaRef.current.value = prompt.query;
            }
            setQuery(prompt.query);
          }} className="box-border flex flex-row items-center justify-center border-1 border-brand-blue-light/60 hover:border-brand-blue-light/100
                        border-dashed rounded-full p-4 text-white/65 hover:text-white hover:cursor-pointer max-w-[280px] bg-white/5 hover:bg-white/10 whitespace-nowrap ">
                      <span className="m-0 p-0 font-normal text-center">{prompt.label}</span>
                    </button>)}
                  {featureFlags?.promptLibrary && (featureFlags?.promptLibrary as unknown as {
            Category: string;
            Prompt: string;
            Purpose: string;
            ShortPrompt: string;
            SubCategory: string;
          }[]).length > 0 && <Tooltip content="Open prompt library" placement="top" delay={100} offset={10} showArrow={true} classNames={{
            content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
            base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
          }}>
                      <button onClick={() => setTriggerPromptLibrary(prev => prev + 1)} className="box-border flex flex-row items-center justify-center border-1 border-brand-blue-light/60 hover:border-brand-blue-light/100
                          border-solid rounded-full p-3 text-white/65 hover:text-white hover:cursor-pointer max-w-[280px] bg-white/5 hover:bg-white/10">
                        <DotsThreeOutlineIcon size={25} />
                      </button>
                    </Tooltip>}
                </div>
            </div>
      // </Walkthru>
      }
        </div>}
    </div>;
};